import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { renderInline } from "../logic/inlineMarkdown";
import { primarySpeaker } from "../logic/npcSpeaker";
import { useAdminMode } from "../logic/useAdminMode";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { E32 } from "../theme/endesga32";
import { pixelTheme } from "../theme/pixel";
import { simplePanelStyle } from "../theme/simplePanel";
import { bevelShadow } from "../theme/styles";
import { AdminFlagTogglePanel } from "./AdminFlagTogglePanel";
import { ChallengeFightModal } from "./ChallengeFightModal";
import { ChallengePanel } from "./ChallengePanel";
import { NpcPortrait, PortraitHatching } from "./NpcPortrait";
import { RewardsModal } from "./RewardsModal";

// Top-level world-route view for an `kind: npc` page. Owns the
// dialogue-tree state machine (which node is active) and the
// challenge-fight modal (which challenge is selected). Hands rendering
// off to NpcDialoguePanel + ChallengePanel + ChallengeFightModal.

interface Props {
  npcId: string;
}

interface DialogueView {
  text: string;
  mood: string | null;
  /** Cross-node options (transitions). Empty on hub nodes that only
   *  carry hub branches. */
  options: api.NpcDialogueOption[];
  /** Hub branches — pure navigation buttons on a hub. Empty on non-hub
   *  nodes. A hub may also carry options, in which case both render. */
  branches: api.NpcHubBranch[];
  /** Speaker rendered in the dialogue panel. */
  speaker: api.NpcSpeaker;
  /** Node id of whatever the view is rendering. `pickNpcOption` /
   *  once-key uses this + the option label, so it has to be the
   *  authored id of the node whose option-or-branch the player is
   *  about to click. */
  rootNodeId: string;
  /** Active challenge set at this view, copied from the rootNodeId's
   *  authored `challenges: <set_id>` field. The challenge panel
   *  renders only when this is non-null. */
  challengeSetId: string | null;
  /** Hub id to auto-Continue back to when the player is on a reply
   *  node that has no options of its own. Set when a hub branch is
   *  picked; cleared on any non-Continue navigation. Null on top-level
   *  views (where the synthetic Continue button doesn't render). */
  returnHubId: string | null;
}

// Post-battle resume key on `location.state`. The battle screen
// attaches the server-resolved `resume_node` to the history entry it
// pushes back to the NPC URL. NpcView reads it on mount; any other
// navigation to the same URL is a different history entry that
// won't carry this state, so the dialogue falls through to the
// NPC's normal default-node resolution.
type NpcViewLocationState = {
  resumeNodeId?: string | null;
};

function viewFromNode(
  node: api.NpcDialogueNode,
  npc: api.NpcPayload,
  returnHubId: string | null = null,
): DialogueView {
  return {
    text: node.text,
    mood: node.mood,
    options: node.options,
    branches: node.branches,
    speaker: node.speaker ?? primarySpeaker(npc),
    rootNodeId: node.id,
    challengeSetId: node.challenge_set_id,
    returnHubId,
  };
}

export function NpcView({ npcId }: Props) {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<api.NpcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<DialogueView | null>(null);
  const [challengeIdx, setChallengeIdx] = useState<number | null>(null);
  const [granted, setGranted] = useState<api.NpcGrantedEntry[] | null>(null);
  const adminMode = useAdminMode();

  // `isActive` lets the mount effect cancel a load whose fetch is still
  // in flight when the effect re-runs. Direct callers (admin reset / flag
  // toggle) pass nothing and always apply. Default is always-active.
  const load = useCallback(
    async (isActive: () => boolean = () => true) => {
      try {
        const r = await api.getNpc(npcId);
        // A superseded load must not write state. Under StrictMode the
        // mount effect fires twice, so two `getNpc`s race; without this
        // guard the slower one re-applies the resume node AFTER the
        // player has already clicked a dialogue option, snapping the
        // view back and eating that first click.
        if (!isActive()) return;
        setData(r);

        // Post-battle resume: the battle screen navigates back with a
        // `resumeNodeId` in location.state. Honor it if it exists in
        // the NPC's tree; otherwise fall through to the server's
        // override-resolved `current_node`. State lives on this single
        // history entry, so any other navigation lands at the normal
        // default.
        const resumeId = (location.state as NpcViewLocationState | null)?.resumeNodeId;
        const resumeNode = resumeId ? r.dialogue.nodes[resumeId] : null;

        if (resumeNode) {
          setView(viewFromNode(resumeNode, r.npc));
        } else {
          setView(viewFromNode(r.dialogue.current_node, r.npc));
        }
      } catch (e) {
        if (!isActive()) return;
        setError(e instanceof Error ? e.message : "Failed to load NPC.");
      }
    },
    [npcId, location.state],
  );

  useEffect(() => {
    let active = true;
    void load(() => active);
    return () => {
      active = false;
    };
  }, [load]);

  async function onBranch(branch: api.NpcHubBranch) {
    if (!data || !view) return;
    if (branch.kind === "leave") {
      if (branch.has_side_effects) {
        try {
          await api.pickNpcOption(npcId, view.rootNodeId, branch.index);
        } catch (e) {
          setError(e instanceof Error ? e.message : "Could not apply branch.");
          return;
        }
      }
      navigate(-1);
      return;
    }

    const hubId = view.rootNodeId;

    if (!branch.has_side_effects) {
      navigateToNode(branch.goto, data, hubId);
      return;
    }

    try {
      const r = await api.pickNpcOption(npcId, hubId, branch.index);
      setData(r);
      navigateToNode(branch.goto, r, hubId);
      if (r.granted && r.granted.length > 0) setGranted(r.granted);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply branch.");
    }
  }

  async function onOption(opt: api.NpcDialogueOption) {
    if (!data || !view) return;

    if (opt.kind === "leave") {
      if (opt.has_side_effects) {
        try {
          await api.pickNpcOption(npcId, view.rootNodeId, opt.index ?? 0);
        } catch (e) {
          setError(e instanceof Error ? e.message : "Could not apply option.");
          return;
        }
      }
      navigate(-1);
      return;
    }
    // `kind: challenge` is legacy — the panel now renders from the
    // node's `challenges: <set_id>`. Treat a stray one as a no-op.
    if (opt.kind === "challenge") return;
    if (opt.kind !== "basic" || !opt.goto) return;

    if (opt.has_side_effects) {
      try {
        const r = await api.pickNpcOption(npcId, view.rootNodeId, opt.index ?? 0);
        setData(r);
        navigateToNode(opt.goto, r, null);
        if (r.granted && r.granted.length > 0) setGranted(r.granted);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not apply option.");
      }
      return;
    }

    navigateToNode(opt.goto, data, null);
  }

  function onContinueToHub() {
    if (!data || !view || !view.returnHubId) return;
    navigateToNode(view.returnHubId, data, null);
  }

  // Shared navigation: set the view to the named node's payload. When
  // the destination node doesn't exist (typo / mid-edit), we leave the
  // view alone so the player isn't stranded on a blank panel.
  function navigateToNode(
    targetId: string,
    source: api.NpcResponse,
    returnHubId: string | null,
  ) {
    const target = source.dialogue.nodes[targetId];
    if (!target) return;
    setView(viewFromNode(target, source.npc, returnHubId));
  }

  // The active challenge set, if any, comes from the current dialogue
  // node's `challenge_set_id`. Each authored node decides whether the
  // panel renders below it — the clerk's intro nodes don't, so the
  // Tordrin stepper never appears there. An NPC can also declare
  // multiple sets and route each to a different node.
  const activeChallengeSet: api.NpcChallengeSet | null = (() => {
    if (!data || !view) return null;
    const setId = view.challengeSetId;
    if (!setId) return null;
    return data.challenge_sets[setId] ?? null;
  })();

  if (error) {
    return (
      <div
        style={{
          padding: 10,
          border: `2px solid ${E32.scarlet}`,
          color: E32.scarlet,
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          letterSpacing: theme.letterSpacing,
        }}
      >
        {error}
      </div>
    );
  }

  if (!data || !view) return null;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <NpcDialoguePanel view={view} />

      {(() => {
        // Branches + cross-node options render together as one button
        // stack under the speech panel — both are "buttons the player
        // can click." When the current view is a reply node (entered
        // from a hub branch) AND has no authored options of its own,
        // we synthesize a "Continue" that returns to the originating
        // hub. The hub re-renders with the once-taken branch filtered.
        const visibleOptions = view.options.filter((o) => o.kind !== "challenge");
        const showContinue =
          !!view.returnHubId &&
          view.branches.length === 0 &&
          visibleOptions.length === 0;
        const hasAny =
          view.branches.length > 0 || visibleOptions.length > 0 || showContinue;
        return (
          hasAny && (
            <DialogueOptions
              branches={view.branches}
              options={visibleOptions}
              onBranch={onBranch}
              onOption={onOption}
              showContinue={showContinue}
              onContinue={onContinueToHub}
            />
          )
        );
      })()}

      {activeChallengeSet && (
        <ChallengePanel
          challenges={activeChallengeSet}
          onPickChallenge={(i) => setChallengeIdx(i)}
          resetsInSeconds={data.resets_in_seconds}
          onReset={
            adminMode
              ? async () => {
                  try {
                    await api.resetAdminNpcChallenges(npcId);
                    await load();
                  } catch (e) {
                    setError(
                      e instanceof Error ? e.message : "Reset failed.",
                    );
                  }
                }
              : undefined
          }
        />
      )}

      {adminMode && data.npc.flag_ids.length > 0 && (
        <AdminFlagTogglePanel
          flagIds={data.npc.flag_ids}
          flagState={data.flag_state}
          onToggle={async (flag, nextOn) => {
            try {
              if (nextOn) {
                await api.adminSetFlag(flag, 1);
              } else {
                await api.adminUnsetFlag(flag);
              }
              await load();
            } catch (e) {
              setError(e instanceof Error ? e.message : "Flag toggle failed.");
            }
          }}
        />
      )}

      {granted && (
        <RewardsModal entries={granted} onClose={() => setGranted(null)} />
      )}

      {challengeIdx !== null && activeChallengeSet && (
        <ChallengeFightModal
          npcId={npcId}
          npc={data.npc}
          setId={activeChallengeSet.set_id}
          entry={activeChallengeSet.entries[challengeIdx]}
          onClose={() => setChallengeIdx(null)}
          onSkipped={(skipResumeNodeId) => {
            // Admin-skip credits the win and returns the same
            // `resume_node` the real battle path would have. Re-fetch
            // the NPC so overrides / flags re-evaluate, then jump to
            // the resume node if the server gave us one.
            setChallengeIdx(null);
            void (async () => {
              try {
                const r = await api.getNpc(npcId);
                setData(r);
                const skipResumeNode = skipResumeNodeId
                  ? r.dialogue.nodes[skipResumeNodeId]
                  : null;
                if (skipResumeNode) {
                  setView(viewFromNode(skipResumeNode, r.npc));
                } else {
                  setView(viewFromNode(r.dialogue.current_node, r.npc));
                }
              } catch (e) {
                setError(
                  e instanceof Error ? e.message : "Failed to refresh NPC.",
                );
              }
            })();
          }}
          onStarted={(sid) =>
            navigate(`/battle/${sid}`, {
              state: { returnTo: location.pathname },
            })
          }
        />
      )}
    </div>
  );
}

function NpcDialoguePanel({
  view,
}: {
  view: DialogueView;
}) {
  const theme = pixelTheme;
  const { narrow } = useResponsiveLayout();
  const speaker = view.speaker;
  // Non-primary speakers render their portrait on the right of the
  // dialogue panel, so the page's "main" character keeps the visual
  // anchor on the left and a guest speaker (e.g. the dwarven
  // ambassador walked out to the embassy lobby) reads as a swap.
  const portraitOnRight = !speaker.is_primary;

  const portrait = <DialoguePortrait speaker={speaker} view={view} />;
  const text = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            color: theme.inkFaint,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {(speaker.role || "").toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 16,
            color: theme.ink,
            marginTop: 4,
            letterSpacing: theme.letterSpacing,
          }}
        >
          {speaker.name.toUpperCase()}
        </div>
      </div>
      <div
        style={{
          fontSize: 18,
          fontFamily: theme.fontBody,
          color: theme.ink,
          lineHeight: 1.4,
          whiteSpace: "pre-wrap",
          minHeight: 60,
        }}
      >
        {renderInline((view.text ?? "").trim())}
      </div>
    </div>
  );

  return (
    <div
      style={{
        ...simplePanelStyle(theme),
        padding: 14,
        display: "grid",
        // Portrait left + dialogue right on desktop, stacked on narrow
        // (the 180px portrait would otherwise crowd the dialogue).
        // For non-primary speakers, swap to dialogue-left + portrait-
        // right so a speaker change reads instantly.
        gridTemplateColumns: narrow
          ? "1fr"
          : portraitOnRight
            ? "1fr 180px"
            : "180px 1fr",
        gap: 16,
        justifyItems: narrow ? "center" : "stretch",
      }}
    >
      {narrow || !portraitOnRight ? (
        <>
          {portrait}
          {text}
        </>
      ) : (
        <>
          {text}
          {portrait}
        </>
      )}
    </div>
  );
}

function DialoguePortrait({
  speaker,
  view,
}: {
  speaker: api.NpcSpeaker;
  view: DialogueView;
}) {
  const portraitMood = view.mood ?? speaker.default_mood ?? null;

  return (
    <NpcPortrait
      faction={speaker.faction}
      npcId={speaker.portrait_id}
      mood={portraitMood}
    >
      <PortraitHatching />
    </NpcPortrait>
  );
}

function DialogueOptions({
  branches,
  options,
  onBranch,
  onOption,
  showContinue,
  onContinue,
}: {
  branches: api.NpcHubBranch[];
  options: api.NpcDialogueOption[];
  onBranch: (branch: api.NpcHubBranch) => void;
  onOption: (opt: api.NpcDialogueOption) => void;
  showContinue: boolean;
  onContinue: () => void;
}) {
  const theme = pixelTheme;
  // Leaves always render last regardless of authored position — branches
  // first (non-leave), then non-leave options, then Continue, then any
  // leave entries from either list.
  const nonLeaveBranches = branches.filter((b) => b.kind !== "leave");
  const nonLeaveOptions = options.filter((o) => o.kind !== "leave");
  const leaveBranches = branches.filter((b) => b.kind === "leave");
  const leaveOptions = options.filter((o) => o.kind === "leave");
  return (
    <section style={{ display: "grid", gap: 8 }}>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 11,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        DIALOGUE OPTIONS
      </div>
      <div style={{ display: "grid", gap: 6 }}>
        {nonLeaveBranches.map((br) => (
          <DialogueButton
            key={`b:${br.label}`}
            label={br.label}
            kind="basic"
            onClick={() => onBranch(br)}
          />
        ))}
        {nonLeaveOptions.map((opt) => (
          <DialogueButton
            key={`o:${opt.label}`}
            label={opt.label}
            kind={opt.kind}
            onClick={() => onOption(opt)}
          />
        ))}
        {showContinue && (
          <DialogueButton
            key="continue"
            label="Continue"
            kind="basic"
            onClick={onContinue}
          />
        )}
        {leaveBranches.map((br) => (
          <DialogueButton
            key={`lb:${br.label}`}
            label={br.label}
            kind="leave"
            onClick={() => onBranch(br)}
          />
        ))}
        {leaveOptions.map((opt) => (
          <DialogueButton
            key={`lo:${opt.label}`}
            label={opt.label}
            kind="leave"
            onClick={() => onOption(opt)}
          />
        ))}
      </div>
    </section>
  );
}

// One vertical-stack dialogue button. Shared by hub branches and
// cross-node options — the only behavioral difference is the
// onClick, dispatched by the parent.
function DialogueButton({
  label,
  kind,
  onClick,
}: {
  label: string;
  kind: "basic" | "leave" | "challenge";
  onClick: () => void;
}) {
  const theme = pixelTheme;
  const startsItalic = label.startsWith("*");
  // Different left-edge accent per kind so the player has a strong
  // visual cue that "Challenge to a duel" isn't just another reply.
  const accent = (() => {
    switch (kind) {
      case "leave":
        return theme.inkFaint;
      case "challenge":
        return E32.scarlet;
      default:
        return E32.cobalt;
    }
  })();
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "12px 14px",
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        borderLeftWidth: 6,
        borderLeftColor: accent,
        boxShadow: bevelShadow,
        cursor: "pointer",
        fontFamily: theme.fontBody,
        fontSize: 18,
        color: theme.ink,
        textAlign: "left",
      }}
    >
      <span style={{ fontStyle: startsItalic ? "italic" : "normal" }}>
        {renderInline(label)}
      </span>
    </button>
  );
}
